import { People } from '@mui/icons-material';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { GroupMessageResponse } from '../models/groupMessageResponse';
import { GroupMessageEmailStatusDataGrid } from './groupMessageEmailStatusDataGrid';
import { GroupMessageEmailStatusSearchFilter } from '../models/groupMessageEmailStatusSearchFilter';

type GroupMessageEmailStatusModalProps = {
  modalOpen: boolean;
  groupMessageEmailResponse: GroupMessageResponse;
  onSave: () => void;
  onClose: () => void;
};

export const GroupMessageEmailStatusModal = (props: GroupMessageEmailStatusModalProps) => {
  const { modalOpen, groupMessageEmailResponse, onSave, onClose } = props;
  const { t } = useTranslation(['common', 'groupMessage']);

  const sortedCirculars = useMemo(() => {
    if (!groupMessageEmailResponse?.circulars) return [];
    return [...groupMessageEmailResponse.circulars].sort((a, b) => {
      const da = a.createdTimeStamp ? new Date(a.createdTimeStamp).getTime() : 0;
      const db = b.createdTimeStamp ? new Date(b.createdTimeStamp).getTime() : 0;
      return db - da;
    });
  }, [groupMessageEmailResponse]);

  const [selectedCircularId, setSelectedCircularId] = useState<number | ''>(() => (sortedCirculars.length > 0 ? sortedCirculars[0].circular_id : ''));

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleChangeCircular = useCallback((event: SelectChangeEvent<number | ''>) => {
    const { value } = event.target;
    setSelectedCircularId(value === '' ? '' : Number(value));
  }, []);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('groupMessage:sms.deliveryStatus') as string}
      dialogSubTitle={
        <Typography variant='body2' sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 500 }}>
          <span>{groupMessageEmailResponse.circularName}</span>
          <People fontSize='small' sx={{ fontSize: '1rem' }} />
          <span>{groupMessageEmailResponse.selectionName || groupMessageEmailResponse.speedFormText}</span>
        </Typography>
      }
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box className='section-inset' sx={{ p: 2 }}>
          <GridWrapper>
            {sortedCirculars.length > 1 && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size='small'>
                  <InputLabel id='circular-select-label'>{t('groupMessage:deliveryStatusList.selectSend') as string}</InputLabel>
                  <Select
                    labelId='circular-select-label'
                    value={selectedCircularId}
                    label={t('groupMessage:deliveryStatusList.selectSend') as string}
                    onChange={handleChangeCircular}
                  >
                    {sortedCirculars.map((c) => {
                      const dateLabel =
                        c.completedTimeStamp && dayjs(c.completedTimeStamp).isValid()
                          ? dayjs(c.completedTimeStamp).format('YYYY-MM-DD HH:mm:ss')
                          : '-';
                      const isTest = (c.subject || c.circularName || '').startsWith('(TEST)');
                      const testPrefix = isTest ? '(TEST) ' : '';
                      return (
                        <MenuItem key={c.circular_id} value={c.circular_id}>
                          {`${testPrefix}${dateLabel} — ${c.totalMails ?? 0} ${t('groupMessage:deliveryStatusList.recipients') as string}`}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              {selectedCircularId !== '' && (
                <GroupMessageEmailStatusDataGrid
                  loadingCallback={() => ConsoleHelper.log('X')}
                  searchFilter={{ filter: '', circular_id: selectedCircularId } as unknown as GroupMessageEmailStatusSearchFilter}
                />
              )}
            </Grid>
          </GridWrapper>
        </Box>
      }
      dialogActions={
        <Button variant='outlined' disableElevation onClick={onHandleClose}>
          {t('common:button.cancel') as string}
        </Button>
      }
    />
  );
};
